import { useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import type { BookSelectionStep } from "./useBookSelectionSteps";

interface StepHistoryState {
    bookSelectionStep: BookSelectionStep;
    /** 0 is the entry the book selection opened on. */
    bookSelectionIndex: number;
}

/** The history entries this page knows: the current one's index, and the step of each by index. */
interface StepEntries {
    index: number;
    steps: Array<BookSelectionStep | undefined>;
}

const readState = (state: unknown): Partial<StepHistoryState> =>
    typeof state === "object" && state !== null ? (state as Partial<StepHistoryState>) : {};

const readOpeningEntries = (state: unknown): StepEntries => {
    const { bookSelectionStep, bookSelectionIndex = 0 } = readState(state);
    const steps: Array<BookSelectionStep | undefined> = [];
    steps[bookSelectionIndex] = bookSelectionStep;
    return { index: bookSelectionIndex, steps };
};

/** Whether Back goes to an earlier step of the book selection rather than leaving it. */
export const isOnLaterBookSelectionStep = (state: unknown): boolean => (readState(state).bookSelectionIndex ?? 0) > 0;

interface StepHistoryOptions {
    step: BookSelectionStep;
    /** Shows a step again when the teacher goes back or forward through the history. */
    onStepRestore: (step: BookSelectionStep) => void;
}

/**
 * Puts each step the teacher opens into the browser history, so Back (the page's button or the browser's)
 * goes one step back instead of leaving the book selection.
 */
export const useStepHistory = (options: StepHistoryOptions) => {
    const history = useHistory();
    const entriesRef = useRef<StepEntries>();
    entriesRef.current ??= readOpeningEntries(history.location.state);
    const entries = entriesRef.current;
    const pathnameRef = useRef(history.location.pathname);
    const optionsRef = useRef(options);
    optionsRef.current = options;

    // The opening entry carries its step too, so Back still finds it after the page was left and reopened.
    useEffect(() => {
        if (entries.index === 0) {
            entries.steps[0] = options.step;
            const state: StepHistoryState = { bookSelectionStep: options.step, bookSelectionIndex: 0 };
            history.replace({ ...history.location, state });
        }
    }, [entries, history, options.step]);

    useEffect(
        () =>
            history.listen((location, action) => {
                if (action !== "POP" || location.pathname !== pathnameRef.current) {
                    return;
                }
                const state = readState(location.state);
                entries.index = state.bookSelectionIndex ?? 0;
                if (state.bookSelectionStep) {
                    entries.steps[entries.index] = state.bookSelectionStep;
                    optionsRef.current.onStepRestore(state.bookSelectionStep);
                }
            }),
        [entries, history]
    );

    /** Records a step the teacher opens; going back to one already behind them goes back through the history. */
    const openStep = (step: BookSelectionStep) => {
        const currentStep = optionsRef.current.step;
        if (step === currentStep) {
            return;
        }

        const earlierIndex = step < currentStep ? entries.steps.slice(0, entries.index).lastIndexOf(step) : -1;
        if (earlierIndex !== -1) {
            history.go(earlierIndex - entries.index);
            return;
        }

        entries.index += 1;
        entries.steps = [...entries.steps.slice(0, entries.index), step];
        const state: StepHistoryState = { bookSelectionStep: step, bookSelectionIndex: entries.index };
        history.push({ ...history.location, state });
    };

    return openStep;
};
